import { DailyTotals } from '@/types'
import { PlanData } from '@/services/analyticsService'
import { isWeeklyDepartmentTableEnabled, type StudioCityRef } from '@/lib/partnerFeatures'
import type { WeeklyPlanVsFactRow } from '@/hooks/useWeeklyPlanVsFact'
import { DailyPeriodMetricsTableClassic } from './DailyPeriodMetricsTableClassic'
import { WeeklyDepartmentMetricsTable } from './WeeklyDepartmentMetricsTable'

/**
 * Развилка между двумя версиями таблицы метрик периода на экранах аналитики:
 *  - переделанная (WeeklyDepartmentMetricsTable) — только пилотный город (Вологда),
 *  - классическая (DailyPeriodMetricsTableClassic) — все остальные.
 *
 * Признак пилота захардкожен во фронте (src/lib/partnerFeatures.ts): в БД его нет.
 * Развилка живёт в одном месте, чтобы экран студии и сводный экран не разъехались.
 */
interface DailyPeriodMetricsTableProps {
  dailyData: DailyTotals[]
  planData: PlanData | null
  weeklyPlans: WeeklyPlanVsFactRow[]
  /** Города студий выборки: переделанная таблица включается, только если все они пилотные. */
  cities?: StudioCityRef[]
  studioId?: number | null
  studioIds?: number[]
  canEditDecisions?: boolean
  canViewDecisions?: boolean
  missingPlanStudios?: string[]
  zeroRevenueStudios?: string[]
  zeroPlanRevenueStudios?: string[]
  hideDelta?: boolean
  hideDeltaStudioCount?: number
  currency?: string
  selectedRangeMode?: 'period' | 'week' | 'last_3_days'
  selectedRangeWeekNumber?: number | null
  onSelectedWeekNumberChange?: (weekNumber: number) => void
}

export function DailyPeriodMetricsTable({
  dailyData,
  planData,
  weeklyPlans,
  cities = [],
  studioId = null,
  studioIds,
  canEditDecisions = false,
  canViewDecisions = canEditDecisions,
  missingPlanStudios = [],
  zeroRevenueStudios = [],
  zeroPlanRevenueStudios = [],
  hideDelta = false,
  hideDeltaStudioCount = 1,
  currency = 'RUB',
  selectedRangeMode = 'period',
  selectedRangeWeekNumber = null,
  onSelectedWeekNumberChange
}: DailyPeriodMetricsTableProps) {
  if (isWeeklyDepartmentTableEnabled(cities)) {
    return (
      <WeeklyDepartmentMetricsTable
        dailyData={dailyData}
        planData={planData}
        weeklyPlans={weeklyPlans}
        studioId={studioId}
        studioIds={studioIds}
        canEditDecisions={canEditDecisions}
        canViewDecisions={canViewDecisions}
        missingPlanStudios={missingPlanStudios}
        zeroRevenueStudios={zeroRevenueStudios}
        zeroPlanRevenueStudios={zeroPlanRevenueStudios}
        hideDelta={hideDelta}
        hideDeltaStudioCount={hideDeltaStudioCount}
        currency={currency}
        selectedRangeMode={selectedRangeMode}
        selectedRangeWeekNumber={selectedRangeWeekNumber}
        onSelectedWeekNumberChange={onSelectedWeekNumberChange}
      />
    )
  }

  return (
    <DailyPeriodMetricsTableClassic
      dailyData={dailyData}
      planData={planData}
      weeklyPlans={weeklyPlans}
      missingPlanStudios={missingPlanStudios}
      zeroRevenueStudios={zeroRevenueStudios}
      zeroPlanRevenueStudios={zeroPlanRevenueStudios}
      hideDelta={hideDelta}
      hideDeltaStudioCount={hideDeltaStudioCount}
      currency={currency}
    />
  )
}
